/**
 * SessionModal Component
 *
 * Modal for session selection and new session creation.
 * Displays draft list when drafts exist, session type selection for new sessions.
 */

// 1. External dependencies
import React, { useState, useMemo, useEffect } from 'react';
import { Calendar, Activity, FileText, Eye, History, Trash2, AlertTriangle, type LucideIcon } from 'lucide-react';
import { parseDate, type DateValue } from '@internationalized/date';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  Chip,
  DatePicker,
  CardHeader
} from '@enfomentis/ui';
import { getTodayInTimezone } from '@/utils/dateHelpers';

// 3. Type imports
import type { SessionDraft } from '@/types/transient/SessionDraft';

/** Session type derived from SessionDraft to stay in sync */
export type SessionType = SessionDraft['sessionType'];

/** Configuration for session type display - defines canonical order and styling */
const SESSION_TYPE_CONFIG: Record<SessionType, {
  label: string;
  description: string;
  icon: LucideIcon;
  iconColorClass: string;
  borderColorClass: string;
}> = {
  live: {
    label: 'Live',
    description: 'Real money trading',
    icon: Activity,
    iconColorClass: 'text-success',
    borderColorClass: 'border-success hover:border-success-400',
  },
  paper: {
    label: 'Paper',
    description: 'Simulated trading',
    icon: FileText,
    iconColorClass: 'text-primary',
    borderColorClass: 'border-primary hover:border-primary-400',
  },
  observation: {
    label: 'Observation',
    description: 'Watching markets',
    icon: Eye,
    iconColorClass: 'text-secondary',
    borderColorClass: 'border-secondary hover:border-secondary-400',
  },
  replay: {
    label: 'Replay',
    description: 'Historical review',
    icon: History,
    iconColorClass: 'text-warning',
    borderColorClass: 'border-warning hover:border-warning-400',
  },
};

/** Canonical order for session types */
const SESSION_TYPE_ORDER: SessionType[] = ['live', 'paper', 'observation', 'replay'];

export interface SessionModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Modal open/close handler */
  onOpenChange: () => void;
  /** Available session drafts (pre-filtered, no tombstones) */
  drafts: SessionDraft[];
  /** Available session types for new session selection */
  sessionTypeOptions: SessionType[];
  /** Handler for starting a new session with selected type and date */
  onStartNew: (sessionType: SessionType, date: string) => void;
  /** Handler for resuming an existing draft */
  onResume: (draftId: string) => void;
  /** Handler for deleting a draft (soft delete). Throws on failure. */
  onDelete: (draftId: string) => Promise<void>;
  /** Today's date string (YYYY-MM-DD) for "today" highlight */
  todayDateString: string;
  /** User's display timezone (e.g., 'America/New_York') for date picker default */
  displayTimezone: string;
}

/**
 * Session Selection Modal
 *
 * Features:
 * - Session type selection for new sessions
 * - Resume existing drafts list
 * - Draft details (date, phase, session type, last updated)
 * - "Today" highlight for current day drafts
 * - Inline delete with confirmation
 */
/** Convert DateValue to YYYY-MM-DD string */
const dateValueToString = (dateValue: DateValue): string => {
  const year = dateValue.year;
  const month = String(dateValue.month).padStart(2, '0');
  const day = String(dateValue.day).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const SessionModal: React.FC<SessionModalProps> = ({
  isOpen,
  onOpenChange,
  drafts,
  sessionTypeOptions,
  onStartNew,
  onResume,
  onDelete,
  todayDateString,
  displayTimezone
}) => {
  // State for inline delete confirmation
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Clear confirmation and loading state when modal closes to prevent stale views on reopen
  useEffect(() => {
    if (!isOpen) {
      setConfirmingDeleteId(null);
      setDeletingId(null);
    }
  }, [isOpen]);

  /** Handle draft deletion with loading state and error handling */
  const handleDelete = async (draftId: string): Promise<void> => {
    setDeletingId(draftId);
    try {
      await onDelete(draftId);
      // Success: close confirmation view
      setConfirmingDeleteId(null);
    } catch {
      // Error: keep confirmation view open so user can retry
      // Parent component handles toast notification
    } finally {
      setDeletingId(null);
    }
  };

  /** Handle resume - just calls onResume (no cleanup needed with card replacement pattern) */
  const handleResume = (draftId: string): void => {
    onResume(draftId);
  };

  // Use todayDateString prop as single source of truth for "today"
  // Falls back to computing from displayTimezone if prop is empty
  const effectiveTodayString = useMemo(
    () => todayDateString || getTodayInTimezone(displayTimezone),
    [todayDateString, displayTimezone]
  );

  // Sort drafts: primary by trading date (desc), secondary by creation time (desc)
  const sortedDrafts = useMemo(() => {
    return [...drafts].sort((a, b) => {
      // Primary: trading date descending (fallback to empty string for corrupted data)
      const dateCompare = (b.date ?? '').localeCompare(a.date ?? '');
      if (dateCompare !== 0) return dateCompare;
      // Secondary: creation time descending
      return (b.metadata?.createdAt ?? '').localeCompare(a.metadata?.createdAt ?? '');
    });
  }, [drafts]);

  // Count drafts per date to know when to show creation time
  const dateCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const draft of drafts) {
      const date = draft.date ?? '';
      counts[date] = (counts[date] || 0) + 1;
    }
    return counts;
  }, [drafts]);
  const [selectedDate, setSelectedDate] = useState<DateValue>(() => parseDate(effectiveTodayString));

  // Filter and order session types through config (skip unknown types)
  const availableSessionTypes = SESSION_TYPE_ORDER.filter(
    (type) => sessionTypeOptions.includes(type) && SESSION_TYPE_CONFIG[type]
  );

  const hasDrafts = drafts.length > 0;

  /** Map SessionDraft.status to display-friendly phase label */
  const getPhaseLabel = (status: SessionDraft['status']): string => {
    switch (status) {
      case 'pre-trading':
        return 'Pre-Trading';
      case 'live':
        return 'Live Trading';
      case 'post-trading':
        return 'Post-Trading';
      case 'review':
        return 'Review';
      case 'shelved':
        return 'Shelved';
      default:
        return 'Unknown';
    }
  };

  /** Map SessionDraft.status to chip color */
  const getPhaseColor = (status: SessionDraft['status']): "default" | "primary" | "secondary" | "success" | "warning" | "danger" => {
    switch (status) {
      case 'pre-trading':
        return 'warning';
      case 'live':
        return 'success';
      case 'post-trading':
        return 'primary';
      case 'review':
        return 'secondary';
      case 'shelved':
        return 'default';
      default:
        return 'default';
    }
  };

  /** Format YYYY-MM-DD date string for display (uses displayTimezone, falls back to local) */
  const formatDate = (dateString: string): string => {
    // Append T12:00:00 to avoid UTC midnight boundary issues
    const date = new Date(dateString + 'T12:00:00');
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      ...(displayTimezone && { timeZone: displayTimezone })
    };
    return date.toLocaleDateString('en-US', options);
  };

  /** Format ISO timestamp as time string (e.g., "9:15 AM") - uses displayTimezone, falls back to local */
  const formatCreationTime = (isoTimestamp: string): string => {
    const date = new Date(isoTimestamp);
    const options: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...(displayTimezone && { timeZone: displayTimezone })
    };
    return date.toLocaleTimeString('en-US', options);
  };

  /** Get chip color for session type */
  const getSessionTypeChipColor = (sessionType: SessionType): "default" | "primary" | "secondary" | "success" | "warning" | "danger" => {
    const colorMap: Record<SessionType, "default" | "primary" | "secondary" | "success" | "warning" | "danger"> = {
      live: 'success',
      paper: 'primary',
      observation: 'secondary',
      replay: 'warning'
    };
    return colorMap[sessionType] || 'default';
  };

  /** Get display label for draft card */
  const getDraftLabel = (draft: SessionDraft): string => {
    // Base: "Today" or formatted date
    const base = draft.date === effectiveTodayString ? 'Today' : formatDate(draft.date ?? '');

    // Append creation time if multiple drafts on same date
    if ((dateCounts[draft.date] ?? 0) > 1 && draft.metadata?.createdAt) {
      return `${base} · ${formatCreationTime(draft.metadata.createdAt)}`;
    }

    return base;
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="2xl"
      placement="center"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <h2 className="text-xl font-bold text-text-primary">
            {hasDrafts ? 'Trading Session' : 'Start Trading Session'}
          </h2>
          <p className="text-sm text-text-secondary">
            {hasDrafts
              ? 'Start a new session or resume an existing one'
              : 'Select the type of session you want to start'}
          </p>
        </ModalHeader>
        
        <ModalBody>
          <div className="space-y-2 md:space-y-4">

            {/* Session Type Selection Grid */}
            <Card className="space-y-2">
              <CardHeader className="text-sm font-medium text-text-primary uppercase tracking-wide">
                Start New Session
              </CardHeader>
              <CardBody className="space-y-2">
                <DatePicker
                  label="Session Date"
                  value={selectedDate}
                  onChange={(value) => value && setSelectedDate(value)}
                  className="flex-1 w-full"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {availableSessionTypes.map((sessionType) => {
                    const config = SESSION_TYPE_CONFIG[sessionType];
                    const IconComponent = config.icon;
                    return (
                      <Card
                        key={sessionType}
                        isPressable
                        isHoverable
                        onPress={() => onStartNew(sessionType, dateValueToString(selectedDate))}
                        className={`border-1 ${config.borderColorClass} transition-colors min-h-18`}
                      >
                        <CardBody className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-10 h-10 bg-default-100 rounded-full shrink-0">
                              <IconComponent size={20} className={config.iconColorClass} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-text-primary">{config.label}</h4>
                              <p className="text-xs text-text-secondary truncate">
                                {config.description}
                              </p>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    );
                  })}
                </div>
                </CardBody>
            </Card>

            {/* Draft List */}
            {sortedDrafts.length > 0 && (
              <Card className="space-y-2">
                <CardHeader className="text-sm font-medium text-text-primary uppercase tracking-wide">
                  Resume Previous Session
                </CardHeader>
                <CardBody className="space-y-2">
                {sortedDrafts.map((draft) => {
                  const isToday = draft.date === effectiveTodayString;
                  const isConfirmingDelete = confirmingDeleteId === draft.id;
                  const isDeleting = deletingId === draft.id;
                  return (
                    <div key={draft.id} className="flex items-center gap-2">
                      <Card
                        isPressable={!isConfirmingDelete}
                        isHoverable={!isConfirmingDelete}
                        {...(!isConfirmingDelete && { onPress: () => handleResume(draft.id) })}
                        className={`transition-colors flex-1 hover:bg-default-50 ${
                          isToday && !isConfirmingDelete ? 'border-primary border-1' : ''
                        }`}
                      >
                        <CardBody className="p-4">
                          {/* Unified layout - details always visible */}
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-4 min-w-0">
                              {/* Icon swaps based on state */}
                              <div className={`flex items-center justify-center w-10 h-10 rounded-full shrink-0 ${
                                isConfirmingDelete ? 'bg-danger-100' : 'bg-default-100'
                              }`}>
                                {isConfirmingDelete ? (
                                  <AlertTriangle size={20} className="text-danger" />
                                ) : (
                                  <Calendar size={18} className="text-text-secondary" />
                                )}
                              </div>
                              <h4 className="font-medium text-text-primary truncate">
                                {getDraftLabel(draft)}
                              </h4>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Chip
                                color={getSessionTypeChipColor(draft.sessionType)}
                                variant="flat"
                                size="sm"
                              >
                                {SESSION_TYPE_CONFIG[draft.sessionType]?.label ?? draft.sessionType}
                              </Chip>
                              <Chip
                                color={getPhaseColor(draft.status)}
                                variant="flat"
                                size="sm"
                              >
                                {getPhaseLabel(draft.status)}
                              </Chip>
                              {/* Confirmation buttons appear when confirming */}
                              {isConfirmingDelete && (
                                <>
                                  <Button
                                    variant="light"
                                    size="sm"
                                    onPress={() => setConfirmingDeleteId(null)}
                                    isDisabled={deletingId !== null}
                                    autoFocus
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    color="danger"
                                    variant="solid"
                                    size="sm"
                                    onPress={() => handleDelete(draft.id)}
                                    isLoading={isDeleting}
                                    isDisabled={deletingId !== null}
                                  >
                                    Delete
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                      {/* Delete button outside Card to avoid button-in-button nesting */}
                      {!isConfirmingDelete && (
                        <Button
                          isIconOnly
                          variant="light"
                          color="danger"
                          size="sm"
                          aria-label="Delete draft"
                          className="min-w-11 min-h-11 shrink-0"
                          onPress={() => setConfirmingDeleteId(draft.id)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  );
                })}
                </CardBody>
              </Card>
            )}
          </div>
        </ModalBody>
        
        <ModalFooter>
          <Button variant="light" onPress={onOpenChange}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};